import { FC, Fragment } from "react";
import {
  IncreaseOfShareCapitalForm,
  SubscriberForCapitalIncrease,
} from "./increaseOfShareCapitalDomain";
import { observer } from "mobx-react";
import { NumberField } from "@/dbot/common/commonComponents";
import {
  calculateNumberOfSharesWithNonCashContribution,
  DebtContributionModel,
} from "./debtContributionDomain";
import { Box, Grid, GridItem, VBox } from "@/components-v2/Box";
import { CURRENCY_NOK } from "@/utils/currency";

import { useLocaleFormat, useTranslation } from "@/stores/RootStoreContext";
import { MTextInput } from "@/components-v2/TextInput";
import { Dialog } from "@/components-v2/Dialog";
import { Icon } from "@/components-v2/Icon";
import { IconButton, LinkButton } from "@/components-v2/Button";
import { confirmAction, thunk } from "@/events/eventsDomain";
import { DropdownMenu } from "@/components-v2/Dropdown";
import { MSelect } from "@/components-v2/Select";
import { findShareTypeName } from "@/common/share";
import { percentOf } from "@/utils/numbers";

const DebtContribution: FC<{ form: IncreaseOfShareCapitalForm }> = observer(({ form }) => {
  const { t } = useTranslation();
  const { formatNumber } = useLocaleFormat();

  return (
    <>
      <VBox gap={"md"}>
        <VBox>
          <div className={"text-lg"}>
            <b>{t("subscriber.debtConversion")}</b>
          </div>
          <div className={"text-sm"}>{t("subscriber.debtConversion.info")}</div>
        </VBox>
        {form.hasDebtContribution ? (
          <Grid alignItems={"center"}>
            {form.subscribersWithDebtContributions.map((subscriber) => {
              return (
                <Fragment key={subscriber.subscriberid}>
                  <GridItem xs={6}>
                    <Box alignItems={"center"}>
                      <Icon name={"article"} />
                      {subscriber.nameField.inputValue}
                    </Box>
                  </GridItem>
                  <GridItem xs={2} textAlign={"right"}>
                    {formatNumber(
                      subscriber.debtContributionModel.maxAmountToBeConverted.toNumberOrZero(),
                    )}
                  </GridItem>
                  <GridItem xs={4} textAlign={"right"}>
                    <LinkButton
                      label={{ key: "edit" }}
                      icon={"edit"}
                      onClick={() => form.openDebtContributionDialog(subscriber)}
                      disabled={form.isProjectLocked}
                    />
                    <IconButton
                      icon={"trash"}
                      tooltip={{ key: "delete" }}
                      onClick={() =>
                        form.dispatch(
                          confirmAction({
                            text: {
                              key: "subscriber.contributionInKind.delete.confirm",
                            },
                            onConfirm: [thunk(() => subscriber.setDebtContribution())],
                          }),
                        )
                      }
                      disabled={form.isProjectLocked}
                    />
                  </GridItem>
                </Fragment>
              );
            })}
            <GridItem xs={6}>
              {form.debtContributionOptions.length > 0 && (
                <DropdownMenu
                  disabled={form.isProjectLocked}
                  button={{ type: "link", label: { key: "subscriber.select" }, icon: "plus" }}
                  items={form.debtContributionOptions.map((subscriber) => {
                    return {
                      type: "item",
                      label: subscriber.nameField.inputValue,
                      isDisabled: form.isProjectLocked,
                      onSelect: () => form.openDebtContributionDialog(subscriber),
                    };
                  })}
                />
              )}
            </GridItem>
            <GridItem xs={2} textAlign={"right"}>
              {formatNumber(form.totalDebtContribution)}
            </GridItem>
          </Grid>
        ) : (
          <Box>
            <DropdownMenu
              disabled={form.isProjectLocked || form.debtContributionOptions.length === 0}
              button={{
                type: "link",
                label: { key: "subscriber.select" },
                icon: "plus",
                tooltip:
                  form.debtContributionOptions.length === 0
                    ? { key: "subscriber.debtConversion.select.disabled" }
                    : undefined,
              }}
              items={form.debtContributionOptions.map((subscriber) => {
                return {
                  type: "item",
                  label: subscriber.nameField.inputValue,
                  onSelect: () => form.openDebtContributionDialog(subscriber),
                };
              })}
            />
          </Box>
        )}
      </VBox>
      <hr />
    </>
  );
});

export const AddDebtContributionDialog: FC<{
  selectedSubscriber: SubscriberForCapitalIncrease;
  model: DebtContributionModel;
  onClose: () => void;
}> = observer(({ selectedSubscriber, model, onClose }) => {
  const { t } = useTranslation();
  return (
    <Dialog
      maxWidth={"sm"}
      title={selectedSubscriber.nameField.inputValue}
      onCancel={onClose}
      isSubmitDisabled={model.isEmpty}
      onSubmit={() => {
        if (model.validate()) {
          selectedSubscriber.setDebtContribution(
            model.toDebtContribution(),
            model.address.validatedValue,
          );
          onClose();
        }
      }}
    >
      <VBox gap={"md"}>
        <Box>
          {t("subscriber.kindContribution.totalSubscription", {
            currency: CURRENCY_NOK,
            totalPrice: {
              type: "number",
              value: model.totalPriceForSubscriber(),
            },
          })}
        </Box>
        <b>{t("subscriber.debtConversion.specifyDebt")}</b>
        <NumberField
          prefix={CURRENCY_NOK}
          field={model.totalAmountReceivable}
          label={t("subscriber.debtConversion.totalReceivableTowardsCompany")}
        />
        <NumberField
          prefix={CURRENCY_NOK}
          field={model.maxAmountToBeConverted}
          label={t("subscriber.debtConversion.amount")}
        />
        {model.hasExcessAmount ? (
          <VBox>
            <Box>
              {t("subscriber.debtConversion.excessAmount.title", {
                value: { type: "amount", value: model.excessAmount, currency: CURRENCY_NOK },
              })}
            </Box>
            <MSelect model={model.excessAmountMethod} />
          </VBox>
        ) : (
          t("subscriber.debtConversion.isAllDebtConverted", {
            name: selectedSubscriber.nameField.inputValue,
          })
        )}
        <MTextInput label={{ key: "address" }} model={model.address} />
        <DebtContributionShareAllocationOverview model={model} />
      </VBox>
    </Dialog>
  );
});

export const DebtContributionShareAllocationOverview: FC<{
  model: DebtContributionModel;
}> = observer(({ model }) => {
  const { t, lang } = useTranslation();
  const { formatNumber } = useLocaleFormat();
  return model.maxAmountToBeConverted.toNumberOrZero() > 0 ? (
    <VBox gap={"md"} marginTop={"lg"}>
      <Box justifyContent={"space-between"}>
        <Box>{t("subscriber.debtConversion.received-shares")}</Box>
        {model.showShareTypePriorityOrder && (
          <DropdownMenu
            button={{
              type: "link",
              label: { key: "subscriber.debtConversion.shareTypePriorityOrder" },
            }}
            items={model.shareTypePriorityOrder.options.map((option) => ({
              type: "radio-item",
              id: model.shareTypePriorityOrder.getId(option),
              label: model.shareTypePriorityOrder.getLabel(option),
              onSelect: () => model.shareTypePriorityOrder.setValue(option),
              isSelected: model.shareTypePriorityOrder.isSelected(option),
            }))}
          />
        )}
      </Box>
      {calculateNumberOfSharesWithNonCashContribution(
        model.maxAmountToBeConverted.toNumberOrZero(),
        model.subscriptionsByShareTypePriorityOrder,
      ).map((sharesPerShareType) => (
        <Box justifyContent={"space-between"} key={sharesPerShareType.shareType.sharetypeId}>
          <Box>
            {formatNumber(sharesPerShareType.numberOfShares)}{" "}
            {findShareTypeName(sharesPerShareType.shareType, lang)}
          </Box>
          <Box fg={"subtle"}>
            {t("subscriber.debtConversion.received-shares.description", {
              percentage: {
                type: "percent",
                value: percentOf(
                  sharesPerShareType.numberOfShares,
                  model.subscribedSharesForShareType(sharesPerShareType.shareType),
                ),
              },
              numberOfShares: {
                type: "number",
                value: model.subscribedSharesForShareType(sharesPerShareType.shareType),
              },
            })}
          </Box>
        </Box>
      ))}
    </VBox>
  ) : undefined;
});

export default DebtContribution;
